import { DraftNoteBody } from '@/components/ui/work-tool-cards/DraftNoteBody';
import { useTemplateCreationApprovalGuard } from '@/composition/hosts/templates/template-builder/useTemplateCreationApprovalGuard';
import { useIsNangoIntegrationsEnabled } from '@/features/flags';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import type {
  ApprovalRequestBlockProps,
  ApprovalRequestInput,
  DraftApprovalBodyRenderArgs,
} from '@heidi/agents';
import {
  ApprovalInputDetails,
  ApprovalRequestBlock,
  useMutateResolveApproval,
} from '@heidi/agents';
import type { ProtectedActionChildRunApproval } from '@heidi/agents/contracts';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { useCallback } from 'react';

import { desktopControlApprovalPort } from './desktop-control-approval';

// The main window's approval surface for the template quality attestation.
const useAskHeidiApprovalGuard = () =>
  useTemplateCreationApprovalGuard('ask_heidi_approval');

const renderApprovalDraftBody = ({
  body,
  isStreaming,
  tintAgainst,
  removal,
}: DraftApprovalBodyRenderArgs) => (
  <DraftNoteBody
    markdown={body}
    isStreaming={isStreaming}
    tintAgainst={tintAgainst}
    removal={removal}
  />
);

const renderInputDetails = (approval: ApprovalRequestInput) => (
  <ApprovalInputDetails approval={approval} renderDraftBody={renderApprovalDraftBody} />
);

// The patient-context card's reads: the app's auth verdict and the patients door.
const patientContext: ApprovalRequestBlockProps['patientContext'] = {
  useAuth: useIsAuthenticated,
  useProfile: useQueryPatientProfileById,
};

/**
 * Resolves against the child run: the parent chat's resolver is parent-scoped.
 * Throwing on failure lets the card unlatch and show its own toast.
 */
export const ProtectedActionApproval = ({
  approval,
}: {
  approval: ProtectedActionChildRunApproval;
}) => {
  const { mutateAsync } = useMutateResolveApproval();
  const isNangoIntegrationsEnabled = useIsNangoIntegrationsEnabled();
  const { agentRunId } = approval;

  const onResolve = useCallback<ApprovalRequestBlockProps['onResolve']>(
    async (approvalId, verdict, reason) => {
      const response = await mutateAsync({
        agentRunId,
        approvalId,
        body: { verdict, reason, modified_input: null },
      });
      if (!response.accepted) throw new Error('Approval was not accepted');
    },
    [agentRunId, mutateAsync]
  );

  return (
    <ApprovalRequestBlock
      agentRunId={agentRunId}
      approval={{
        approval_id: approval.approvalId,
        tool_name: approval.toolName,
        tool_input: approval.toolInput,
        tool_call_id: approval.toolCallId,
        toolCallDisplay: approval.toolCallDisplay,
        context: approval.context,
      }}
      onResolve={onResolve}
      isNangoIntegrationsEnabled={isNangoIntegrationsEnabled}
      desktopControl={desktopControlApprovalPort}
      useApprovalGuard={useAskHeidiApprovalGuard}
      renderInputDetails={renderInputDetails}
      patientContext={patientContext}
    />
  );
};
